
import DOMPurify from 'dompurify';
import {computed, defineComponent} from 'vue';
import Autocomplete from '@trevoreyre/autocomplete-js';
import {fetchGeolocations} from "../api/GeolocationApi";
import {fetchBrowserlocation} from "../api/BrowserlocationApi";
import {Geolocation} from "@/models/GeolocationModel";
import {useBrowserlocationComposition} from "@/composables/BrowserlocationComposition";
import {useGeolocationComposition} from "@/composables/GeolocationComposition";
import {useStoreComposition} from "@/composables/StoreComposition";
import {useTranslationComposition} from "@/composables/TranslationComposition";
import {useFilterComposition} from "@/composables/FilterComposition";
import {configuration} from "@/composables/ConfigurationComposition";

export default defineComponent({

  data: function() {
    return {
        windowVar: window
      };
  },

  setup() {
    const hasBrowserLocationPermissionCSSClass = computed(() => {
      return useBrowserlocationComposition.hasBrowserLocationPermission ? 'permission' : 'nopermission'
    });

    const isPendingCSSClass = computed(() => {
      return useBrowserlocationComposition.isBrowserLocationPending ? 'pending' : ''
    });

    const isUsingBrowserLocationCSSClass = computed(() => {
      return useBrowserlocationComposition.isUsingBrowserLocation ? 'browserlocation' : ''
    });

    const isFilterEnabledCSSClass = computed(() => {
      return configuration.enableFilter ? 'is-filterenabled' : '';
    });

    const isEnableCountryDropdown = computed(() => {
      return configuration.enableCountryDropdown;
    });

    const isEnableLocationSearch = computed(() => {
      return configuration.enableLocationSearch;
    });

    const defaultCountry = computed(() => {
      return configuration.defaultCountry;
    });

    return {
        isEnableCountryDropdown: isEnableCountryDropdown,
        isEnableLocationSearch,
        defaultCountry,
      geolocationComposition: useGeolocationComposition,
      storeComposition: useStoreComposition,
      translationComposition: useTranslationComposition,
      browserlocationComposition: useBrowserlocationComposition,
      filterComposition: useFilterComposition,
      hasBrowserLocationPermissionCSSClass,
      isPendingCSSClass,
      isUsingBrowserLocationCSSClass,
      isFilterEnabledCSSClass
    };
  },
  methods: {
    resetInput() {
      (this.$refs.input as HTMLInputElement).value = '';
    },
    reset() {
      this.geolocationComposition.resetPosition();
      this.storeComposition.loadStores();
      this.geolocationComposition.formattedSearch = '';
      this.geolocationComposition.unformattedSearch = '';

      this.resetInput();

      this.browserlocationComposition.isUsingBrowserLocation = false
    }
  },
  mounted() {
    const that = this;
    const input = (this.$refs.input as HTMLInputElement);
    const country = (this.$refs.country as HTMLSelectElement);
    if (navigator.permissions) {
      try {
        navigator.permissions.query({
          name: 'geolocation'
        })
            .then((permissionStatus) => {
              if (permissionStatus.state === 'denied') {
                this.browserlocationComposition.hasBrowserLocationPermission = false;
              }

              permissionStatus.onchange = function () {
                if (this.state === 'denied') {
                  that.reset();

                  that.browserlocationComposition.hasBrowserLocationPermission = false;
                  that.browserlocationComposition.isBrowserLocationPending = false;
                }
              };
            });
      } catch (e) {
        // do nothing
      }
    }

    /**
     * handle click event to request access to browsers geolocation
     */
    (this.$refs.browserlocation as HTMLElement)?.addEventListener('click', () => {
      if (!this.browserlocationComposition.isBrowserLocationPending) {
        this.browserlocationComposition.isBrowserLocationPending = true;

        if (!this.browserlocationComposition.isUsingBrowserLocation) {
          this.browserlocationComposition.isUsingBrowserLocation = true

          navigator.geolocation.getCurrentPosition(async(geolocation) => {
            const country = await fetchBrowserlocation(geolocation.coords.latitude, geolocation.coords.longitude);

            this.geolocationComposition.country = `${country}`;

            this.geolocationComposition.position = {
              lat: geolocation.coords.latitude,
              lng: geolocation.coords.longitude
            };

            if(configuration.enableFilter){
              this.filterComposition.distance = 100;
            }

            this.geolocationComposition.formattedSearch = this.translationComposition.browserLocationTranslation;
            this.geolocationComposition.unformattedSearch = this.translationComposition.browserLocationTranslation;

            this.resetInput();

            this.storeComposition.loadStores();

            window.dispatchEvent(new CustomEvent('kmt-storefinder-geolocationchange', {
              detail: {
                element: this.$refs.browserlocation
              }
            }));

            // only to open filter
            this.$emit('onGeolocationChange');

            this.browserlocationComposition.isBrowserLocationPending = false;
          }, () => {
            this.browserlocationComposition.isBrowserLocationPending = false;
          });
        } else {
          this.reset();

          that.browserlocationComposition.isBrowserLocationPending = false;
        }
      }
    });

    /**
     * handle input event to show loading state
     */
    input.addEventListener('input', () => {
      this.geolocationComposition.beginAutocompleteLoading();
    });

    /**
     * handle keydown event for enter key to load stores for the selected location
     */
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        // @ts-ignore
        const liFirst = this.$refs.list.querySelector('li:first-child');
        // @ts-ignore
        const liSelected = this.$refs.list.querySelector('li[aria-selected]');

        if (liSelected === null && liFirst !== null) {
          liFirst.click();
        }
      }
    });

    /**
     * handle focus event to send custom event
     */
    input.addEventListener('focus', (e) => {
      window.dispatchEvent(new CustomEvent('kmt-storefinder-inputfocus', {
        detail: {
          element: e.target
        }
      }));
    });

    new Autocomplete('#autocomplete', {
      debounceTime: 300,
      autoSelect: true,
      getResultValue: (result: Geolocation) => {
        return result.formatted;
      },
      onUpdate: (result: Geolocation[]) => {
        this.geolocationComposition.lastUpdateResult = result
      },
      /**
       * callback when a suggested location is selected
       *
       * @param result
       */
      onSubmit: (result: Geolocation) => {
        if (result !== undefined && result !== this.geolocationComposition.lastSubmitResult) {
          this.geolocationComposition.country = `${result.country}`;
          this.geolocationComposition.position = {
            lat: result.lat,
            lng: result.lng
          };
          this.geolocationComposition.formattedSearch = result.formatted;
          this.geolocationComposition.lastSubmitResult = result;

          this.filterComposition.distance = 100;

          this.storeComposition.loadStores();

          this.browserlocationComposition.isUsingBrowserLocation = false

          // only open filter
          this.$emit('onSubmit', result);
        }
      },
      /**
       * debounced callback when the input value has been changed to trigger location search
       *
       * @param input
       */
      search: (input: string) => {
          input = DOMPurify.sanitize(input, {USE_PROFILES: {html: false}});

        this.geolocationComposition.beginAutocompleteLoading();
        this.geolocationComposition.unformattedSearch = input;

        return new Promise((resolve) => {
          if (input.length > 0) {
            fetchGeolocations(input, country?.value ?? this.defaultCountry).then((data: Geolocation[]) => {
              resolve(data);

              this.geolocationComposition.addToAutocompleteSuggestList(input, data);
              this.geolocationComposition.endAutocompleteLoading();
            }).catch(() => {
              resolve([]);

              this.geolocationComposition.addToAutocompleteSuggestList(input, []);
              this.geolocationComposition.endAutocompleteLoading();
            })
          } else {
            resolve([]);

            this.geolocationComposition.addToAutocompleteSuggestList(input, []);
            this.geolocationComposition.endAutocompleteLoading();
          }
        });
      }
    });
  }
});
